import React, { useCallback, useMemo } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import { defaultValue } from './FilterConditionInput';
import { translations } from '../translations';
import { Dropdown, DropdownHandle } from '@/components/core/Dropdown';

const messages = defineMessages({
  selectOperator: {
    id: 'filter_condition_operator_input.select_operator',
    defaultMessage: 'Select operator',
  },
});

export const typeOperators: Record<
  FilterCondition['type'],
  FilterCondition['operator'][]
> = {
  boolean: ['known', 'unknown', 'true', 'false'],
  date: [
    'known',
    'unknown',
    'before',
    'after',
    'between',
    'not_between',
    'in_last',
    'not_in_last',
  ],
  enum: ['known', 'unknown', 'eq', 'neq', 'any_of', 'none_of'],
  number: [
    'known',
    'unknown',
    'eq',
    'neq',
    'gt',
    'gte',
    'lt',
    'lte',
    'between',
    'not_between',
    'none_of',
    'any_of',
  ],
  string: [
    'known',
    'unknown',
    'eq',
    'neq',
    'matches',
    'not_matches',
    'none_of',
    'any_of',
  ],
  location: ['known', 'unknown'],
  photo: ['known', 'unknown'],
  'array:number': ['known', 'unknown', 'none_of', 'any_of', 'all_of'],
  'array:string': ['known', 'unknown', 'none_of', 'any_of', 'all_of'],
};

/** The shape of the value an operator expects. */
type OperatorValueKind =
  /** No value at all. */
  | 'none'
  /** A single value. */
  | 'single'
  /** A single ISO 8601 duration string (relative dates). */
  | 'duration'
  /** Exactly two values, forming a range. */
  | 'range'
  /** Any number of values. */
  | 'list';

const operatorValueKinds: Record<
  FilterCondition['operator'],
  OperatorValueKind
> = {
  known: 'none',
  unknown: 'none',
  true: 'none',
  false: 'none',
  eq: 'single',
  neq: 'single',
  matches: 'single',
  not_matches: 'single',
  gt: 'single',
  gte: 'single',
  lt: 'single',
  lte: 'single',
  before: 'single',
  after: 'single',
  in_last: 'duration',
  not_in_last: 'duration',
  between: 'range',
  not_between: 'range',
  any_of: 'list',
  none_of: 'list',
  all_of: 'list',
};

interface FilterConditionOperatorInputProps {
  filterCondition: FilterCondition;
  schema: EntitySchema;
  onChange: (filterCondition: FilterCondition) => void;
}

const FilterConditionOperatorInput = React.forwardRef<
  DropdownHandle,
  FilterConditionOperatorInputProps
>(({ filterCondition, schema, onChange }, ref) => {
  const intl = useIntl();
  const operators: FilterCondition['operator'][] = useMemo(
    () => typeOperators[filterCondition.type],
    [filterCondition.type],
  );

  const options = useMemo(() => {
    return operators.map((operator) => ({
      id: operator,
      label: intl.formatMessage(translations[operator]) || operator,
      value: operator.toString(),
    }));
  }, [operators, intl]);

  const handleChange = useCallback(
    (operator: FilterCondition['operator']) => {
      const { value: previousValue, ...conditionWithoutValue } =
        filterCondition as FilterCondition & { value?: unknown };

      const valueKind = operatorValueKinds[operator];
      if (valueKind === 'none') {
        onChange({ ...conditionWithoutValue, operator } as FilterCondition);
        return;
      }

      // The previous value can only be carried over to an operator expecting
      // the same shape, otherwise it has to start over from a default.
      const keepPreviousValue =
        previousValue !== undefined &&
        operatorValueKinds[filterCondition.operator] === valueKind;

      onChange({
        ...conditionWithoutValue,
        operator,
        value: keepPreviousValue
          ? previousValue
          : defaultValue(
              filterCondition.type,
              operator,
              schema.definition.propertiesLookup[filterCondition.field]
                .alternatives,
            ),
      } as FilterCondition);
    },
    [filterCondition, schema.definition.propertiesLookup, onChange],
  );

  return (
    <Dropdown
      ref={ref}
      id="operator-dropdown"
      placeholder={intl.formatMessage(messages.selectOperator)}
      options={options}
      value={options.find(
        (option) => option.value === filterCondition.operator.toString(),
      )}
      onValueChange={(value) =>
        handleChange(value.value as FilterCondition['operator'])
      }
    />
  );
});
FilterConditionOperatorInput.displayName = 'FilterConditionOperatorInput';

export default FilterConditionOperatorInput;
